import { SafeSessionStorage } from '#pplx/frontend/browser-storage/sessionStorage.js';
/**
 * Active-account identity for the multi-account web feature.
 *
 * The active account (a NextAuth userId) lives in `sessionStorage` so each tab can be a
 * different account. This is the lowest layer of the storage stack: it owns the
 * active-account lookup + key namespacing so `localStorage.ts` can scope keys to the
 * active account without importing `accountStorage.ts` (which would create a cycle).
 *
 * Until multi-account is activated nothing writes the active-account key, so `namespaceKey`
 * returns keys unchanged and the whole stack behaves exactly as it does today.
 */
export const ACTIVE_ACCOUNT_STORAGE_KEY = 'pplx-active-account';
const NAMESPACE_PREFIX = 'pplx:account:';
/**
 * The localStorage key prefix for an account's namespace (e.g. `pplx:account:{accountId}:`).
 * `namespaceKey` builds on this; callers that need to match a whole namespace (e.g. clearing
 * one account) should use this rather than re-deriving the prefix.
 */
export function accountNamespacePrefix(accountId) {
    return `${NAMESPACE_PREFIX}${accountId}:`;
}
export function getActiveAccountId() {
    return SafeSessionStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
}
/**
 * Multi-account: the request header that binds a call to an account, so the backend resolves that
 * account's session cookie. Defaults to this tab's active account; pass an explicit `accountId` to
 * attribute the call to a specific account regardless of the tab (e.g. registering the shared
 * web-push subscription for every signed-in account). Returns an empty object when there's no
 * account (single-account user, feature off, or a worker with no sessionStorage) — spread into a
 * request's headers, the absent header leaves the backend on the primary cookie (unchanged
 * behavior). Shared by every outbound path (plain fetch, SSE, and GraphQL) so they all carry it.
 */
export function getAccountHeaders(accountId = getActiveAccountId()) {
    if (!accountId) {
        return {};
    }
    return { 'X-Pplx-Account': accountId };
}
export function setActiveAccountId(accountId) {
    return SafeSessionStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, accountId);
}
/**
 * Clears this tab's active-account pointer. Used on sign-out so a stale pointer can't make a
 * subsequent same-tab sign-in send `X-Pplx-Account` for a now-cookieless account.
 */
export function clearActiveAccountId() {
    return SafeSessionStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
}
/**
 * Returns the account-namespaced form of `key`, or `key` unchanged when no account is
 * active (primary account / feature disabled). An explicit `accountId` overrides the
 * active-account lookup, which migration relies on.
 */
export function namespaceKey(key, accountId = getActiveAccountId()) {
    if (!accountId) {
        return key;
    }
    return `${accountNamespacePrefix(accountId)}${key}`;
}
//# sourceMappingURL=activeAccount.js.map